import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { Icon } from '../components/Icon'
import { ErrorBox, TableScroll } from '../components/ui'
import { useApi, type Resource } from '../lib/api'
import { useI18n } from '../lib/i18n'
import type { AdjudicationJob, AssignmentSummary, ReviewAssignment } from '../lib/types'

/**
 * The reader's jobs at every site: open ones first, finished ones folded at the end; without open jobs,
 * what jobs are and where to work meanwhile. Beside them, on wide screens, the reader's place among each
 * site's readers (PRJ-25) and how reading goes.
 */
export function AssignmentsPage() {
  const { t } = useI18n()
  const assignments = useApi<AssignmentSummary[]>('/assignments')
  const adjudications = useApi<AdjudicationJob[]>('/adjudications')
  const reviews = useApi<ReviewAssignment[]>('/reviews')
  const open = assignments.data?.filter((assignment) => assignment.state !== 'finished') ?? []
  const finished = assignments.data?.filter((assignment) => assignment.state === 'finished') ?? []
  // Their place among each site's readers (AUD-09).
  const sites = [...new Map(assignments.data?.map((assignment) => [assignment.site_id, assignment.site_name])).entries()]
  return (
    <div className="reading-page">
      <h1>{t('myAssignments')}</h1>
      <div className="reading-main">
        <ErrorBox error={assignments.error} retry={assignments.reload} />
        {assignments.data && (open.length > 0 ? <JobsTable assignments={open} /> : <NoOpenJobs finished={finished.length > 0} />)}
        <Listed title={t('adjudication')} list={adjudications}>
          {(job) => (
            <li key={job.job_id}>
              <span>
                {job.site_name} · {job.job_name}
              </span>
              <span className="muted small">{t('adjudicatedImages', { count: String(job.adjudicated_images) })}</span>
              <Link className="button push" to={`/adjudicate/${job.job_id}`}>
                {t('adjudicate')}
              </Link>
            </li>
          )}
        </Listed>
        <Listed title={t('reviews')} list={reviews}>
          {(assignment) => (
            <li key={assignment.id}>
              <span>
                {assignment.site_name} · {assignment.job_name} · {assignment.reader}
              </span>
              <span className="badge quiet">{t(`job_${assignment.state}`)}</span>
              <Link className="button push" to={`/reviews/${assignment.id}`}>
                {t('review')}
              </Link>
            </li>
          )}
        </Listed>
        {finished.length > 0 && (
          <details className="finished-jobs">
            <summary>{t('finishedAssignments', { count: String(finished.length) })}</summary>
            <JobsTable assignments={finished} />
          </details>
        )}
      </div>
      <aside className="reading-aside" aria-label={t('readingAside')}>
        {sites.length > 0 && (
          <section className="card">
            <h2>
              <Icon name="contributors" /> {t('yourContributions')}
            </h2>
            <p className="muted small">{t('yourContributionsHint')}</p>
            <ul className="link-list">
              {sites.map(([id, name]) => (
                <li key={id}>
                  <Link to={`/contributions/${id}`} aria-label={t('myContributions', { site: name })}>
                    {name}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
        <section className="card">
          <h2>{t('readingHow')}</h2>
          <ol className="reading-steps">
            <li>{t('readingHowAssignments')}</li>
            <li>{t('readingHowFinish')}</li>
            <li>{t('readingHowCompared')}</li>
          </ol>
        </section>
      </aside>
    </div>
  )
}

/** Where the jobs would be: what they are, and the images to work on meanwhile. */
function NoOpenJobs({ finished }: { finished: boolean }) {
  const { t } = useI18n()
  return (
    <div className="card empty-state">
      <Icon name="reading" />
      <h2>{t(finished ? 'noOpenAssignments' : 'noAssignments')}</h2>
      <p className="muted">{t('noAssignmentsHint')}</p>
      <Link className="button" to="/images">
        {t('browseImages')}
      </Link>
    </div>
  )
}

/** A list under its heading when it has entries; one that failed to load says so, rather than looking empty. */
function Listed<T>({ title, list, children }: { title: string; list: Resource<T[]>; children: (item: T) => ReactNode }) {
  if (!list.error && !list.data?.length) return null
  return (
    <>
      <h2 className="section-title">{title}</h2>
      {list.error ? <ErrorBox error={list.error} retry={list.reload} /> : <ul className="list">{list.data!.map(children)}</ul>}
    </>
  )
}

function JobsTable({ assignments }: { assignments: AssignmentSummary[] }) {
  const { t } = useI18n()
  return (
    <div className="card">
      <TableScroll label={t('myAssignments')}>
        <table>
          <thead>
            <tr>
              <th>{t('site')}</th>
              <th>{t('job')}</th>
              <th>{t('status')}</th>
              <th>{t('progress')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {assignments.map((assignment) => (
              <tr key={assignment.id}>
                <td>{assignment.site_name}</td>
                <td>{assignment.job_name}</td>
                <td>
                  {assignment.closed_at ? (
                    <span className="badge warn">{t('jobClosed')}</span>
                  ) : (
                    <span className={assignment.state === 'finished' ? 'badge ok' : 'badge'}>{t(`job_${assignment.state}`)}</span>
                  )}
                </td>
                <td>
                  <progress max={assignment.images} value={assignment.finished_images} />{' '}
                  {t('finishedOf', { finished: String(assignment.finished_images), total: String(assignment.images) })}
                </td>
                <td>
                  <Link to={`/assignments/${assignment.id}`}>{t('openJob')}</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </div>
  )
}
